<template>
  <div class="ai-transcript">
    <div class="ai-transcript__column">
      <template v-for="row in rows">
        <!-- Skill changes and blocks sit outside the conversation, on the user's
             side, because the user is who did them. -->
        <div
          v-if="row.type === 'skill-change'"
          :key="row.key"
          class="ai-marker ai-marker--skill"
        >
          <span class="ai-marker__rule"></span>
          <span class="ai-marker__text">{{ row.text }}</span>
        </div>

        <div
          v-else-if="row.type === 'blocked'"
          :key="row.key"
          class="ai-marker ai-marker--blocked"
        >
          <span class="ai-marker__rule"></span>
          <span class="ai-marker__text">
            <i class="fa fa-exclamation-triangle"></i>
            A message was blocked: {{ row.reason }}
          </span>
        </div>

        <!-- User messages are short utterances, so they get a bubble. Clamped at
             six rendered lines so a pasted document does not eat the transcript. -->
        <div v-else-if="row.type === 'user'" :key="row.key" class="ai-turn ai-turn--user">
          <div class="ai-turn__stack">
          <div class="ai-bubble" :class="{ 'ai-bubble--failed': Boolean(row.failure) }">
            <div
              :ref="`userText-${row.key}`"
              class="ai-bubble__text"
              :class="{ 'ai-bubble__text--clamped': !expanded[row.key] }"
            >{{ row.text }}</div>
            <button
              v-if="overflowing[row.key]"
              type="button"
              class="ai-bubble__expander"
              @click="toggleExpanded(row.key)"
            >
              {{ expanded[row.key] ? "Show less" : "Show more" }}
            </button>
          </div>

          <!-- Attached to the message it belongs to, rather than reported somewhere
               else on the screen. A refusal that leaves the transcript looking normal
               reads as "sent and ignored", which is the one thing that did not
               happen. -->
          <div v-if="row.failure" class="ai-turn__failure">
            <i class="fa fa-exclamation-circle"></i>
            <span class="ai-turn__failure-text">
              {{ row.failure.message }}
              <span v-if="row.failure.code" class="ai-turn__failure-code">{{ row.failure.code }}</span>
            </span>
            <button type="button" class="ai-turn__retry" @click="$emit('retry', row.message)">
              {{ row.failure.isBlock ? "Edit and resend" : "Try again" }}
            </button>
          </div>
          </div>
        </div>

        <!-- Assistant answers are documents, not utterances. No bubble: plain text
             across the reading column, so headings and tables read as themselves. -->
        <!-- State is resolved here rather than in `rows` so the ticking clock does
             not invalidate the computed — otherwise every answer in the transcript
             would be re-parsed from markdown once a second while one is pending. -->
        <div v-else-if="row.type === 'assistant'" :key="row.key" class="ai-turn ai-turn--assistant">
          <div v-if="assistantState(row.message) === 'complete'">
            <div class="ai-answer" v-html="row.html"></div>

            <!-- The provider stopped early, almost always at the output-token cap. The
                 text is real and is saved; it just trails off. Rendered silently, a
                 half-sentence reads as the model being broken. -->
            <div v-if="row.message.status === 'interrupted'" class="ai-answer__cut">
              <i class="fa fa-scissors"></i>
              This answer was cut short at its length limit. Ask for the rest, or ask for
              something shorter.
            </div>
          </div>

          <!-- Mid-answer. Deliberately NOT rendered as markdown: half-arrived syntax —
               an unclosed fence, a lone asterisk — renders as debris and then reflows
               when it completes. Plain text grows steadily and turns into the formatted
               answer once `done` lands. -->
          <div
            v-else-if="assistantState(row.message) === 'streaming'"
            class="ai-answer ai-answer--streaming"
          >{{ row.message.text }}<span class="ai-cursor"></span></div>

          <div
            v-else-if="assistantState(row.message) === 'working'"
            class="ai-answer ai-answer--working"
          >
            <span class="ai-dots"><span></span><span></span><span></span></span>
            <span class="ai-answer__note">Working on it{{ elapsedLabel(row.message) }}</span>
          </div>

          <div
            v-else-if="assistantState(row.message) === 'lost'"
            class="ai-answer ai-answer--lost"
          >
            <i class="fa fa-unlink"></i>
            This answer was lost before it finished saving. Send the message again.
          </div>

          <div v-else class="ai-answer ai-answer--failed">
            <i class="fa fa-exclamation-circle"></i>
            {{ assistantFailure(row.message) }}<span v-if="row.message.errorCode">
              ({{ row.message.errorCode }})</span>.
            Send the message again.
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { render } from "@/utils/ai/markdown";
import { describeRefusal, reasonForCode } from "@/utils/ai/refusal";

/**
 * The client's patience for a message that has not reached a terminal status.
 *
 * The generation cap is 300s, so anything still pending past this is not slow — the
 * pod was redeployed or crashed mid-answer and no terminal status was ever written.
 * Never set this below the cap: doing so abandons answers still legitimately being
 * produced. There is no cleanup job in v1 by design; the row's `createdAt` carries
 * the same information the job would have written.
 */
const PATIENCE_MS = 330 * 1000;

/** How close to the foot of the transcript still counts as following the answer. */
const FOLLOW_THRESHOLD_PX = 80;

/** Distinct answer texts to keep parsed markdown for, per mounted transcript. */
const MARKDOWN_CACHE_LIMIT = 300;

export default {
  name: "MessageTranscript",

  props: {
    messages: {
      type: Array,
      default: () => [],
    },
    /**
     * Skill name by skill version id, for messages sent in this session.
     *
     * History returns `skillVersionId` only, and the skills endpoint deliberately
     * exposes no version ids, so a change loaded from history can be detected but
     * not named. Those render as "Skill changed" until `skillName` lands on
     * MessageView server-side.
     */
    skillNames: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      expanded: {},
      overflowing: {},
      now: Date.now(),
      clock: null,
      // Whether the reader was at the foot of the transcript when the last update began.
      // Read before the DOM is patched, acted on after — see `beforeUpdate`.
      wasAtBottom: true,
    };
  },

  created() {
    // Not in `data()`: nothing renders from it, and making a Map reactive would cost on
    // every entry for no gain.
    this.markdownCache = new Map();
  },

  computed: {
    /**
     * Flattened render list. Markers are derived here rather than stored anywhere —
     * a skill change is the difference between two consecutive messages, not an
     * event, which is why changing the picker without sending produces nothing.
     */
    rows() {
      const rows = [];
      let previousUserSkillVersion = null;

      this.messages.forEach((message) => {
        const key = message.id || `seq-${message.seq}`;

        if (message.role === "user") {
          // Optimistic messages carry no skill version — the backend resolves the
          // live one at send time. Deriving a marker from `undefined` would show a
          // spurious "Skill changed" and then poison the comparison for the real row
          // that replaces it a moment later.
          if (message.isLocal) {
            rows.push({
              type: "user",
              key,
              text: message.text || "",
              // Present only on a message the server refused. Carried on the row so the
              // bubble can say so in place.
              failure: message.failure || null,
              message,
            });
            return;
          }

          if (
            previousUserSkillVersion !== null &&
            message.skillVersionId !== previousUserSkillVersion
          ) {
            const name = this.skillNames[message.skillVersionId];
            rows.push({
              type: "skill-change",
              key: `${key}-skill`,
              text: name ? `Skill changed to ${name}` : "Skill changed",
            });
          }
          previousUserSkillVersion = message.skillVersionId;

          if (message.blockReason && !message.text) {
            // A blocked message has no body anywhere, by design. The placeholder
            // keeps the conversation coherent when scrolled back through — a silent
            // gap makes the assistant look like it ignored something.
            rows.push({
              type: "blocked",
              key: `${key}-blocked`,
              reason: message.blockReason,
            });
            return;
          }

          // `refused` is a message the gate stopped before the assistant ever saw it —
          // over a spend limit, out of allowance, too long, too fast. It is rendered
          // where it was sent, with the reason attached, rather than as a bare row that
          // looks like every other message in the transcript.
          rows.push({
            type: "user",
            key,
            text: message.text || "",
            failure: describeRefusal(message),
            message,
          });
          return;
        }

        rows.push({
          type: "assistant",
          key,
          message,
          // Nothing is rendered as markdown while it is still arriving — the streaming
          // branch paints the raw text — and finished answers come out of the memo, so a
          // fragment landing thirty times a second does not re-parse the whole
          // transcript thirty times a second.
          html:
            message.text && message.status !== "streaming" ? this.renderCached(message.text) : "",
        });
      });

      return rows;
    },

    hasPendingMessage() {
      return this.messages.some(
        (message) => message.status === "pending" || message.status === "streaming",
      );
    },

    /**
     * What the clamp measurement actually depends on.
     *
     * Measuring is a synchronous read of every user bubble's rendered height, and it was
     * hung off `rows` — which now changes on every fragment of a streaming answer. The
     * clamp only applies to user messages, and none of them change while the assistant
     * is talking, so this is what to watch instead.
     */
    userTextSignature() {
      return this.rows
        .filter((row) => row.type === "user")
        .map((row) => `${row.key}:${(row.text || "").length}`)
        .join("|");
    },
  },

  watch: {
    hasPendingMessage: {
      immediate: true,
      handler(pending) {
        // The clock only needs to run while something is unresolved — it exists to
        // move a message from "working" to "lost" as it crosses the patience
        // threshold, and to tick the elapsed label.
        if (pending) {
          this.startClock();
        } else {
          this.stopClock();
        }
      },
    },

    userTextSignature() {
      this.$nextTick(this.measureOverflow);
    },
  },

  mounted() {
    this.measureOverflow();
    this.scrollToBottom();
  },

  /**
   * Whether to follow the answer as it grows is decided from where the reader already
   * was, and that can only be known before the new text is in the DOM.
   */
  beforeUpdate() {
    this.wasAtBottom = this.isNearBottom();
  },

  updated() {
    if (this.wasAtBottom) {
      this.scrollToBottom();
    }
  },

  beforeDestroy() {
    this.stopClock();
  },

  methods: {
    /**
     * Markdown for an answer, parsed once per distinct text.
     *
     * A finished answer's text never changes, so this hits on every subsequent render.
     * It matters while a stream is running: without it, one fragment arriving re-parses
     * and re-sanitises every completed answer in the transcript.
     *
     * The cache is dropped wholesale once it is large rather than evicted entry by
     * entry — it is scoped to one mounted transcript, and an exact bound is not worth a
     * queue to hold it.
     */
    renderCached(text) {
      if (this.markdownCache.has(text)) {
        return this.markdownCache.get(text);
      }

      if (this.markdownCache.size > MARKDOWN_CACHE_LIMIT) {
        this.markdownCache.clear();
      }

      const html = render(text);
      this.markdownCache.set(text, html);
      return html;
    },

    /**
     * Near enough the bottom to count as reading the newest thing.
     *
     * The tolerance is there because a streaming answer grows between the check and the
     * scroll, and because sub-pixel heights mean an exact comparison is never true.
     */
    isNearBottom() {
      const el = this.$el;
      if (!el) {
        return true;
      }
      return el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_THRESHOLD_PX;
    },

    /**
     * Follow the answer down.
     *
     * Only ever called when the reader was already at the foot of the transcript. Someone
     * who has scrolled up to re-read something earlier is left where they are — being
     * yanked back down mid-sentence by text they are not reading is worse than having to
     * scroll down themselves.
     */
    scrollToBottom() {
      const el = this.$el;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },

    /**
     * Which provider failure this was.
     *
     * A failed *assistant* row means the message reached the provider and the answer did
     * not come back — a different event from a refusal, which never got that far, and
     * worth wording differently so the two are not read as one flaky thing. An
     * unrecognised code falls back to the generic wording rather than borrowing the
     * user-side sentence.
     */
    assistantFailure(message) {
      return reasonForCode(message.errorCode) || "This answer could not be generated";
    },


    /**
     * Which of the five things this answer is.
     *
     * `interrupted` is read as complete, not failed. It means the provider stopped early
     * — almost always at the output-token cap — and the text it did produce is real and
     * saved. Treating it as a failure would throw away an answer the user was charged
     * for and is probably happy with; the note under it says what happened.
     *
     * `streaming` with text is its own state so the fragments can be painted as they
     * arrive. `streaming` without text yet is still "working": the model can spend a
     * long time reading the conversation before its first token.
     */
    assistantState(message) {
      if (message.status === "complete" || message.status === "interrupted") {
        return "complete";
      }
      if (message.status === "failed") {
        return "failed";
      }
      if (message.status === "streaming" && message.text) {
        return "streaming";
      }

      const age = this.now - new Date(message.createdAt).getTime();
      return age > PATIENCE_MS ? "lost" : "working";
    },

    elapsedLabel(message) {
      if (message.status === "complete") {
        return "";
      }

      const seconds = Math.floor((this.now - new Date(message.createdAt).getTime()) / 1000);
      if (seconds < 10 || seconds > PATIENCE_MS / 1000) {
        return "";
      }
      return ` — ${seconds}s`;
    },

    /**
     * The expander only appears when the text is actually cut off. Comparing rendered
     * heights is the only reliable way to know: the clamp counts wrapped lines, so a
     * single long paragraph can overflow six lines without containing a newline.
     */
    measureOverflow() {
      const overflowing = {};

      this.rows.forEach((row) => {
        if (row.type !== "user") {
          return;
        }

        const refs = this.$refs[`userText-${row.key}`];
        const el = Array.isArray(refs) ? refs[0] : refs;
        if (!el) {
          return;
        }

        if (this.expanded[row.key]) {
          overflowing[row.key] = true;
          return;
        }

        overflowing[row.key] = el.scrollHeight > el.clientHeight + 2;
      });

      this.overflowing = overflowing;
    },

    toggleExpanded(key) {
      this.$set(this.expanded, key, !this.expanded[key]);
    },

    startClock() {
      if (this.clock) {
        return;
      }
      this.clock = setInterval(() => {
        this.now = Date.now();
      }, 1000);
    },

    stopClock() {
      if (this.clock) {
        clearInterval(this.clock);
        this.clock = null;
      }
    },
  },
};
</script>

<style scoped>
/* White surface on the page's grey canvas. Padding is deliberately thin so the
   grey reads as an edge rather than a margin. */
/* Transparent: the shimmering silver fill and the green wrap belong to the
   surface wrapper, which also holds the fixed header. */
.ai-transcript {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 12px 8px;
}

/* Prose is the whole output of this tool, so the column is capped for reading
   rather than stretched to the viewport. Collapsing the history rail buys calm,
   not width. */
.ai-transcript__column {
  max-width: 760px;
  margin: 0 auto;
}

/* Markers ---------------------------------------------------------------- */

.ai-marker {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 22px 0 14px;
  font-size: 12px;
  letter-spacing: 0.02em;
}

.ai-marker__rule {
  flex: 1 1 auto;
  height: 1px;
  background: #e3e6ec;
}

.ai-marker__text {
  flex: 0 0 auto;
  white-space: nowrap;
}

/* The answer as it arrives: plain text, so half-arrived markdown does not render as
   debris and then reflow when it completes. Line breaks are honoured so a model writing
   a list still looks like one. */
.ai-answer--streaming {
  white-space: pre-wrap;
  word-break: break-word;
}

/* A block that keeps pace with the text rather than a spinner parked somewhere else:
   the point of streaming is that the answer itself is the progress indicator. */
.ai-cursor {
  display: inline-block;
  width: 7px;
  height: 1em;
  margin-left: 2px;
  vertical-align: text-bottom;
  background: #6eb644;
  animation: ai-cursor-blink 1.1s steps(2, start) infinite;
}

@keyframes ai-cursor-blink {
  to {
    visibility: hidden;
  }
}

/* Reduced motion turns the blink off and leaves the block, which still marks where the
   text has reached. */
@media (prefers-reduced-motion: reduce) {
  .ai-cursor {
    animation: none;
  }
}

/* Under the answer, not in place of it. The text above is real and was paid for; this
   only says why it stops where it does. */
.ai-answer__cut {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 8px 11px;
  border-radius: 8px;
  background: #fdf6ec;
  font-size: 12px;
  color: #8a5a1a;
}

.ai-marker--skill .ai-marker__text {
  color: #8a91a0;
}

.ai-marker--blocked .ai-marker__rule {
  background: #f3c9c9;
}

.ai-marker--blocked .ai-marker__text {
  color: #c0392b;
  font-weight: 500;
}

/* User turns ------------------------------------------------------------- */

.ai-turn {
  display: flex;
  margin-bottom: 22px;
}

.ai-turn--user {
  justify-content: flex-end;
}

/* Tinted against the white transcript. The assistant's answer sits directly on the
   surface with no container, so the bubble is the only thing set apart — which is
   what makes the two sides read differently at a glance. */
.ai-bubble {
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 14px 14px 2px 14px;
  background: #f1f3f7;
  color: #2f3542;
}

.ai-bubble__text {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.55;
}

.ai-bubble__text--clamped {
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Not red text on a red bubble: the message is still the user's own words and stays
   readable. The border and the line beneath it are what carry the failure. */
.ai-bubble--failed {
  border: 1px solid #f3c9c9;
  background: #fdf0f0;
}

/* The turn itself is a row, so the bubble and its explanation need a column of their
   own — otherwise the reason sits beside the message rather than under it. */
.ai-turn__stack {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: 80%;
}

.ai-turn__stack .ai-bubble {
  max-width: 100%;
}

.ai-turn__failure {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: #a3372c;
  text-align: right;
}

.ai-turn__failure-code {
  margin-left: 6px;
  opacity: 0.7;
}

.ai-turn__retry {
  flex: 0 0 auto;
  padding: 0;
  border: none;
  background: none;
  color: #6b7c99;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
}

.ai-bubble__expander {
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 12px;
  color: #5b6b8c;
  cursor: pointer;
}

.ai-bubble__expander:hover {
  text-decoration: underline;
}

/* Assistant turns -------------------------------------------------------- */

.ai-turn--assistant {
  justify-content: flex-start;
}

.ai-answer {
  width: 100%;
  line-height: 1.65;
  color: #2f3542;
}

.ai-answer >>> p {
  margin: 0 0 12px;
}

.ai-answer >>> h1,
.ai-answer >>> h2,
.ai-answer >>> h3,
.ai-answer >>> h4 {
  margin: 20px 0 10px;
  font-weight: 600;
  line-height: 1.3;
}

.ai-answer >>> h1 { font-size: 22px; }
.ai-answer >>> h2 { font-size: 19px; }
.ai-answer >>> h3 { font-size: 16px; }
.ai-answer >>> h4 { font-size: 15px; }

.ai-answer >>> ul,
.ai-answer >>> ol {
  margin: 0 0 12px;
  padding-left: 22px;
}

.ai-answer >>> li {
  margin-bottom: 4px;
}

.ai-answer >>> blockquote {
  margin: 0 0 12px;
  padding: 2px 0 2px 14px;
  border-left: 3px solid #dfe3ea;
  color: #6b7285;
}

.ai-answer >>> code {
  padding: 1px 5px;
  border-radius: 3px;
  background: #f0f2f5;
  font-size: 13px;
}

/* Code and tables are allowed past the reading column — they want the room that
   prose does not. */
.ai-answer >>> pre {
  margin: 0 0 14px;
  padding: 12px 14px;
  border-radius: 6px;
  background: #f6f7f9;
  overflow-x: auto;
}

.ai-answer >>> pre code {
  padding: 0;
  background: none;
}

.ai-answer >>> table {
  width: 100%;
  margin: 0 0 14px;
  border-collapse: collapse;
  font-size: 13px;
  display: block;
  overflow-x: auto;
}

.ai-answer >>> th,
.ai-answer >>> td {
  padding: 7px 10px;
  border: 1px solid #e3e6ec;
  text-align: left;
}

.ai-answer >>> th {
  background: #f6f7f9;
  font-weight: 600;
}

.ai-answer--working,
.ai-answer--lost,
.ai-answer--failed {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #8a91a0;
  font-size: 13px;
}

.ai-answer--lost,
.ai-answer--failed {
  color: #b06a2c;
}

.ai-dots {
  display: inline-flex;
  gap: 4px;
}

.ai-dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #b9bfcb;
  animation: ai-dot 1.3s infinite ease-in-out;
}

.ai-dots span:nth-child(2) { animation-delay: 0.18s; }
.ai-dots span:nth-child(3) { animation-delay: 0.36s; }

@keyframes ai-dot {
  0%, 70%, 100% { opacity: 0.35; }
  35% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .ai-dots span { animation: none; }
}

/* Dark theme ------------------------------------------------------------- */

body.full-dark .ai-bubble {
  background: #2b3243;
  color: #e4e7ee;
}

body.full-dark .ai-answer {
  color: #dfe3ec;
}

body.full-dark .ai-marker__rule {
  background: #333c50;
}

body.full-dark .ai-answer >>> code,
body.full-dark .ai-answer >>> pre {
  background: #232938;
}

body.full-dark .ai-answer >>> th {
  background: #232938;
}

body.full-dark .ai-answer >>> th,
body.full-dark .ai-answer >>> td {
  border-color: #333c50;
}
</style>
